<script>
    import { onMount } from "svelte";

    import debounce from "lodash/debounce";

    import {
        PageHead,
        PageLoading,
        Button,
        CardLayout,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getFormatDate,
        getMFAType,
        getNumber,
        getUserStatus,
        getText,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import { MFAType, UserStatus } from "../../Utils/utilEnums.svelte";

    import {
        ADMIN_EMAIL_CAN_EDIT_STATUS,
        ADMIN_ID_CAN_EDIT_STATUS,
        SEARCH,
        editIcon,
    } from "../../Utils/utilConstants.svelte";

    import { user } from "../../stores";

    export let params = {};

    let isGettingAdmin = false,
        isEdit = false,
        isUpdatingAdmin = false,
        isShowPassword = false,
        isShowConfirmPassword = false,
        isPwdSame = true;

    let admin = {},
        defaultAdmin = {},
        passwordObj = {
            newPwd: "",
            confirmPwd: "",
        };

    const btnOptions = [
        {
            iconName: "editIcon",
            iFont: editIcon,
            iconFunction: () => (isEdit = true),
            hasTooltip: false,
            btnText: "Edit",
        },
    ];

    onMount(async () => {
        if (!$user.email) await loadUserDetails();

        await loadAdminById(params.id);
    });

    const loadUserDetails = async () =>
        await apiMethods.getMethod.getUserById($user.id).then((res) => {
            if (!res || res.error)
                showError(
                    res.error ??
                        "Failed to get user email, please refresh the page and try again and contact support if the issue persists.",
                );
            else $user.email = res.email;
        });

    const loadAdminById = async (id) => {
        isGettingAdmin = true;

        await apiMethods.getMethod
            .getAdminById(id)
            .then((res) => _handleAdmin(res));

        isGettingAdmin = false;
    };

    const editAdmin = () => {
        isEdit = true;
    };

    const saveAdmin = async () => {
        isUpdatingAdmin = true;

        _updateAdminPwd();

        await apiMethods.putMethod.updateAdmin(admin).then((res) => {
            if (res.id) {
                admin = { ...res };
                defaultAdmin = { ...res };
            } else admin = { ...defaultAdmin };
        });

        isUpdatingAdmin = false;
        isEdit = false;
    };

    const cancelEdit = () => {
        isEdit = false;
        admin = { ...defaultAdmin };
    };

    const toggleShowPassword = () => {
        isShowPassword = !isShowPassword;
        _keepShowPasswordStatus(false);
    };

    const toggleShowConfirmPassword = () => {
        isShowConfirmPassword = !isShowConfirmPassword;
        _keepShowPasswordStatus(true);
    };

    const handleInput = () => {
        if (passwordObj.newPwd && passwordObj.confirmPwd)
            isPwdSame = passwordObj.newPwd === passwordObj.confirmPwd;
    };

    // HELPER FUNCTIONS
    const _handleAdmin = (res) => {
        if (res.error) showError(res.error);
        else {
            admin = { ...res };
            defaultAdmin = { ...res };
        }
    };

    const _keepShowPasswordStatus = (isConfirm) => {
        let el = isConfirm
            ? document.getElementById("confirmPassword")
            : document.getElementById("password");

        if (isConfirm) {
            if (isShowConfirmPassword) el.type = "text";
            else el.type = "password";
        } else if (isShowPassword) el.type = "text";
        else el.type = "password";
    };

    const _updateAdminPwd = () => {
        if ($user.id == 1) {
            if (!admin.password && isPwdSame)
                admin.password = passwordObj.newPwd;
            else if (admin.password) {
                if (isPwdSame && passwordObj.newPwd)
                    admin.password = passwordObj.newPwd;
            }
        }
    };
</script>

{#if isGettingAdmin}
    <PageLoading />
{:else}
    <PageHead title={getText(defaultAdmin.fullName)} />

    <CardLayout title="Admin Details" {btnOptions}>
        <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
            <div class="flex flex-col flex-1 gap-y-4 h-auto">
                <div class="flex justify-start gap-x-4 px-2">
                    <div class="title">Name:</div>
                    <div class="content">
                        {#if isEdit}
                            <input
                                type="text"
                                bind:value={admin.fullName}
                                class="input input-sm input-bordered w-full"
                            />
                        {:else}
                            <p>{getText(admin.fullName)}</p>
                        {/if}
                    </div>
                </div>

                <div class="flex gap-x-4 px-2">
                    <div class="title">Email:</div>
                    <div class="content">
                        {#if isEdit}
                            <input
                                type="text"
                                bind:value={admin.email}
                                class="input input-sm input-bordered w-full"
                            />
                        {:else}
                            <p>{getText(admin.email)}</p>
                        {/if}
                    </div>
                </div>

                <div class="flex gap-x-4 px-2">
                    <div class="title">Phone:</div>
                    <div class="content">
                        {#if isEdit}
                            <input
                                type="text"
                                bind:value={admin.phone}
                                name="new-phone"
                                autocomplete="new-phone"
                                class="input input-sm input-bordered w-full"
                            />
                        {:else}
                            <p>{getText(admin.phone)}</p>
                        {/if}
                    </div>
                </div>

                <div class="flex justify-start gap-x-4 px-2">
                    <div class="title">Status:</div>
                    <div class="content">
                        {#if isEdit && ($user.id == ADMIN_ID_CAN_EDIT_STATUS || $user.email == ADMIN_EMAIL_CAN_EDIT_STATUS)}
                            <select
                                bind:value={admin.status}
                                class="input input-sm input-bordered w-full"
                            >
                                {#each UserStatus as status}
                                    <option value={status.id}>
                                        {status.type}
                                    </option>
                                {/each}
                            </select>
                        {:else}
                            <p>{getUserStatus(admin.status)}</p>
                        {/if}
                    </div>
                </div>
                <div class="flex gap-x-4 px-2">
                    <div class="title">MFA:</div>
                    <div class="content">
                        {#if isEdit}
                            <select
                                bind:value={admin.mfa}
                                class="input input-sm input-bordered w-full"
                            >
                                {#each MFAType as mfa}
                                    {#if mfa.id !== 0}
                                        <option
                                            value={mfa.id}
                                            selected={admin.mfa == mfa.id}
                                        >
                                            {mfa.type}
                                        </option>
                                    {/if}
                                {/each}
                            </select>
                        {:else}
                            <p>{getMFAType(admin.mfa)}</p>
                        {/if}
                    </div>
                </div>
            </div>

            <div class="divider lg:divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4 h-auto">
                <div class="flex gap-x-4 px-2">
                    <div class="title">Created date:</div>
                    <p class="content">{getFormatDate(admin.createdDate)}</p>
                </div>

                <div class="flex gap-x-4 px-2">
                    <div class="title">Updated date:</div>
                    <p class="content">{getFormatDate(admin.updatedDate)}</p>
                </div>

                <div class="flex gap-x-4 px-2">
                    <div class="title">Last login date:</div>
                    <p class="content">{getFormatDate(admin.loggedInDate)}</p>
                </div>

                <div class="flex gap-x-4 px-2">
                    <div class="title">Login attempts:</div>
                    <p class="content">{getNumber(admin.loginAttempts)}</p>
                </div>

                {#if isEdit && ($user.id == ADMIN_ID_CAN_EDIT_STATUS || $user.email == ADMIN_EMAIL_CAN_EDIT_STATUS)}
                    <div class="flex gap-x-4 px-2 items-center">
                        <div class="title">Password:</div>
                        <div class="content">
                            <input
                                id="password"
                                type="password"
                                autocomplete="new-password"
                                name="new-password"
                                class="input input-sm input-bordered min-w-36"
                                bind:value={passwordObj.newPwd}
                                on:input={debounce(() => {
                                    handleInput();
                                }, SEARCH.DEBOUNCE_TIME)}
                            />
                            {#if isShowPassword}
                                <i
                                    style="font-size:16px"
                                    class="fa hover:cursor-pointer"
                                    on:click|preventDefault={toggleShowPassword}
                                    >&#xf070;</i
                                >
                            {:else}
                                <i
                                    style="font-size:16px"
                                    class="fa hover:cursor-pointer"
                                    on:click|preventDefault={toggleShowPassword}
                                    >&#xf06e;</i
                                >
                            {/if}
                        </div>
                    </div>

                    <div class="flex gap-x-4 px-2 items-center">
                        <div class="title">Confirm password:</div>
                        <div class="content">
                            <input
                                id="confirmPassword"
                                type="password"
                                autocomplete="new-password"
                                name="new-password"
                                class="input input-sm input-bordered min-w-36"
                                bind:value={passwordObj.confirmPwd}
                                on:input={debounce(() => {
                                    handleInput();
                                }, SEARCH.DEBOUNCE_TIME)}
                            />
                            {#if isShowConfirmPassword}
                                <i
                                    style="font-size:16px"
                                    class="fa hover:cursor-pointer"
                                    on:click|preventDefault={toggleShowConfirmPassword}
                                    >&#xf070;</i
                                >
                            {:else}
                                <i
                                    style="font-size:16px"
                                    class="fa hover:cursor-pointer"
                                    on:click|preventDefault={toggleShowConfirmPassword}
                                    >&#xf06e;</i
                                >
                            {/if}
                        </div>
                    </div>

                    {#if !isPwdSame}
                        <div class="text-error">Password not same</div>
                    {/if}
                {/if}

                {#if isEdit}
                    <div class="flex flex-row gap-x-3 w-full justify-end">
                        <button
                            class="btn btn-secondary flex-1 btn-sm"
                            on:click={cancelEdit}
                            disabled={isUpdatingAdmin}
                        >
                            Cancel Edit
                        </button>
                        <button
                            class="btn btn-primary flex-1 btn-sm"
                            on:click={saveAdmin}
                            disabled={isUpdatingAdmin}
                        >
                            {isUpdatingAdmin ? "Saving" : "Save"}
                        </button>
                    </div>
                {/if}
            </div>
        </div>
    </CardLayout>
{/if}

<style>
    input,
    select {
        background-color: #e5e7eb;
    }

    .title {
        width: 150px;
    }

    .content {
        width: calc(100% - 150px);
    }
</style>
